import { NgForOf } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    ViewChild,
    HostListener,
    input,
    output,
    AfterViewInit,
    inject,
    OnInit,
    Input,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { debounceTime, first, fromEvent } from 'rxjs';

import { MediaImageComponent } from '../media-image/media-image.component';

import { getLanguageisRTL } from '@core/infrastructure/state/core.selectors';
import { MediasPublicModels } from '@features/products/medias';
import { ImageMediaService, MediaModel } from '@shared/media/public_api';

@Component({
    selector: 'media-carousel',
    templateUrl: './media-carousel.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgForOf, MediaImageComponent],
})
export class MediaCarouselComponent implements OnInit, AfterViewInit {
    private readonly store = inject(Store);

    private readonly imageMediaService: ImageMediaService = inject(ImageMediaService);

    @Input() public imageModel: MediaModel;

    @Input() public animationCarousel: boolean;

    @Input() public loadAllImages: boolean;

    @ViewChild('carousel', { static: true }) public carousel: ElementRef;

    @ViewChild('innerCarouselContainer', { static: true }) public innerCarouselContainer: ElementRef;

    public readonly productImages = input.required<MediasPublicModels.PublicMediaResolved[]>();

    public loadedImages: MediasPublicModels.PublicMediaResolved[] = [];

    public readonly clickMedia = output<MouseEvent | TouchEvent>();

    public readonly imageLoaded = output<Event>();

    public readonly imageError = output<Event>();

    private readonly isRTLLanguage: boolean = this.store.selectSignal(getLanguageisRTL)();

    private isDown = false; // Tracks if the user has started clicking or touching the carousel

    private startX: number; // Stores the initial click or touch position on the X-axis

    private scrollLeft: number; // Stores the initial scroll position of the carousel

    private isDragging = false; // Detects if the user is dragging the carousel

    private readonly executedAnimationKey = 'mediaCarouselExecutedAnimation'; // Storage key to know if the initial animation has already been executed

    /**
     * Initializes the component.
     * Loads the initial images into the carousel.
     */
    public ngOnInit(): void {
        this.loadInitialImages();
    }

    /**
     * Executes after the view of the component has been initialized.
     * Sets up the initial animation.
     * Subscribes to the scroll event of the carousel to load the remaining images.
     */
    public ngAfterViewInit(): void {
        this.setupInitialAnimation();

        fromEvent(this.carousel.nativeElement, 'scroll')
            .pipe(
                debounceTime(300),
                first(),
            ).subscribe(() => { this.loadRemainingImages(); });
    }

    /**
     * Starts the drag process on the carousel when detecting a mouse event.
     */
    @HostListener('mousedown', ['$event'])
    public onMouseDown(event: MouseEvent): void {
        event.preventDefault();
        this.isDown = true; // Marks that the user is interacting with the carousel
        this.startX = event.pageX - this.carousel.nativeElement.offsetLeft; // Calculates the initial drag position
        this.scrollLeft = this.carousel.nativeElement.scrollLeft; // Stores the initial scroll position
        this.carousel.nativeElement.classList.add('active'); // Adds a CSS class when dragging starts
    }

    /**
     * Handles the drag movement when the user moves the mouse.
     */
    @HostListener('mousemove', ['$event'])
    public onMouseMove(event: MouseEvent): void {
        if (!this.isDown) return; // Proceed only if the user is interacting

        // Calculates the new drag position
        const x = event.pageX - this.carousel.nativeElement.offsetLeft;

        // Calculate the drag distance
        const walk = x - this.startX;

        // Update the scroll position of the carousel
        this.carousel.nativeElement.scrollLeft = this.scrollLeft - walk;

        // Mark that the user is dragging
        this.isDragging = true;
    }

    /**
     * Ends the drag process on the carousel.
     */
    @HostListener('mouseup', ['$event'])
    @HostListener('mouseleave', ['$event'])
    public onMouseUpLeave(event: MouseEvent): void {
        event.preventDefault();
        this.onUp(event);
        this.carousel.nativeElement.classList.remove('active');
    }

    /**
     * Starts the drag process on the carousel when detecting a touch event.
     */
    @HostListener('touchstart', ['$event'])
    public onTouchStart(event: TouchEvent): void {
        this.isDown = true; // Marks that the user is interacting with the carousel
        this.startX = event.touches[0].pageX - this.innerCarouselContainer.nativeElement.offsetLeft; // Calculates the initial drag position
        this.scrollLeft = this.innerCarouselContainer.nativeElement.scrollLeft; // Stores the initial scroll position
    }

    /**
     * Handles the drag movement when the user touches the screen.
     */
    @HostListener('touchmove', ['$event'])
    public onTouchMove(event: TouchEvent): void {
        if (!this.isDown) return; // Proceed only if the user is interacting

        // Calculates the new drag position
        const x = event.touches[0].pageX - this.innerCarouselContainer.nativeElement.offsetLeft;

        // Calculate the drag distance
        const walk = x - this.startX;

        // Update the scroll position of the carousel
        this.innerCarouselContainer.nativeElement.scrollLeft = this.scrollLeft - walk;

        // Mark that the user is dragging
        this.isDragging = true;
    }

    /**
     * Ends the touch process on the carousel.
     */
    @HostListener('touchend', ['$event'])
    public onTouchEnd(event: TouchEvent): void {
        this.onUp(event);
    }

    /**
     * Emits the image load event.
     */
    public onImageLoad($event): void {
        setTimeout(() => {
            this.imageLoaded.emit($event);
        }, 100);
    }

    /**
     * Emits the image error event.
     */
    public onImageError($event): void {
        this.imageError.emit($event);
    }

    /**
     * Load the remaining images into the carousel after dragging.
     */
    private loadRemainingImages(): void {
        if (this.loadedImages.length < this.productImages().length) {
            const remainingImages = this.productImages().slice(this.loadedImages.length);
            this.loadedImages.push(...remainingImages);
        }
    }

    /**
     * Initializes the carousel with the first two images.
     */
    private loadInitialImages(): void {
        this.loadedImages = this.loadAllImages ? this.productImages() : this.productImages().slice(0, 2);
    }

    /**
     * Resets the drag-related state variables.
     */
    private resetState(): void {
        this.isDown = false;
        this.isDragging = false;
    }

    /**
     * Emits the click event to the parent component.
     */
    private onClickMedia(event: MouseEvent | TouchEvent): void {
        this.clickMedia.emit(event);
    }

    /**
     * Sets up the initial animation for the first image in the carousel.
     * Only executes if it's the first carousel and the animation hasn't been run before.
     */
    private setupInitialAnimation(): void {
        if (this.animationCarousel && this.imageMediaService.isFirstCarousel()) {
            this.animateFirstImage(); // Execute animation only if it's the first carousel and hasn't been executed before
        }
    }

    /**
     * Runs an initial animation for the first carousel by shifting the first image.
     */
    private animateFirstImage(): void {
        const firstImage = this.innerCarouselContainer.nativeElement.querySelector('div[media-image]');
        if (firstImage) {
            const initialScroll = this.innerCarouselContainer.nativeElement.scrollLeft;
            const offset = this.isRTLLanguage ? -100 : 100; // Temporary shift for the first image
            const horizontalMovement = initialScroll + offset;

            // Este timeout es necesario porque se lanza la animación cuando se carga el view del componente,
            // por lo que la imagen puede no estar cargada aún. Habría que cambiar el sistema para lanzar
            // la animación del carrusel del primer producto después de que se cargue su primera imagen.
            setTimeout(() => {
                this.innerCarouselContainer.nativeElement.animate(
                    [
                        { transform: 'translateX(0)' },
                        { transform: `translateX(${-horizontalMovement}px)` },
                        { transform: 'translateX(0)' },
                    ],
                    {
                        duration: 800,
                        easing: 'ease-in-out',
                    }
                );
            }, 3000);
        }
    }

    /**
     * Handles the mouse up or touch end events.
     * If the user has not dragged the carousel, it is considered a click on the image.
     * Resets the state variables to their initial values.
     *
     * @param event The event that triggered the mouse up or touch end
     */
    private onUp(event: MouseEvent | TouchEvent): void {
        if (this.isDown && !this.isDragging) {
            this.onClickMedia(event);
        }
        this.resetState();
    }
}
